import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface ThemeTokens {
  light?: Record<string, string>;
  dark?: Record<string, string>;
}

interface BrandingData {
  logoUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string | null;
  accentColor: string | null;
  themeTokens?: ThemeTokens | null;
  fontFamily?: string | null;
}

export function BrandingPage() {
  const { t } = useTranslation();
  const [logoUrl, setLogoUrl] = useState('');
  const [faviconUrl, setFaviconUrl] = useState('');
  const [primaryColor, setPrimaryColor] = useState('');
  const [accentColor, setAccentColor] = useState('');
  const [themeTokensJson, setThemeTokensJson] = useState('');
  const [themeTokensError, setThemeTokensError] = useState<string | null>(null);
  const [fontFamily, setFontFamily] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    apiClient
      .get<BrandingData>('/branding')
      .then(({ data }) => {
        setLogoUrl(data.logoUrl ?? '');
        setFaviconUrl(data.faviconUrl ?? '');
        setPrimaryColor(data.primaryColor ?? '');
        setAccentColor(data.accentColor ?? '');
        setThemeTokensJson(
          data.themeTokens ? JSON.stringify(data.themeTokens, null, 2) : '',
        );
        setFontFamily(data.fontFamily ?? '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setThemeTokensError(null);

    let parsedTokens: ThemeTokens | null = null;
    if (themeTokensJson.trim()) {
      try {
        parsedTokens = JSON.parse(themeTokensJson);
        if (
          typeof parsedTokens !== 'object' ||
          parsedTokens === null ||
          Array.isArray(parsedTokens)
        ) {
          throw new Error('Theme tokens must be an object');
        }
      } catch (err) {
        setThemeTokensError(
          err instanceof Error ? err.message : 'Invalid JSON',
        );
        return;
      }
    }

    setSaving(true);
    try {
      await apiClient.put('/branding', {
        logoUrl: logoUrl.trim() || null,
        faviconUrl: faviconUrl.trim() || null,
        primaryColor: primaryColor.trim() || null,
        accentColor: accentColor.trim() || null,
        themeTokens: parsedTokens,
        fontFamily: fontFamily.trim() || null,
      });
      setToast('Branding saved successfully');
    } catch {
      setToast('Failed to save branding');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Branding</h1>

      <form
        onSubmit={handleSubmit}
        className="max-w-lg space-y-4 rounded-lg border bg-card p-6"
      >
        <div className="space-y-1">
          <label className="text-sm font-medium">Logo URL</label>
          <Input
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            placeholder="https://example.com/logo.png"
          />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">Favicon URL</label>
          <Input
            value={faviconUrl}
            onChange={(e) => setFaviconUrl(e.target.value)}
            placeholder="https://example.com/favicon.ico"
          />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">Primary Color</label>
          <div className="flex items-center gap-2">
            <Input
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              placeholder="#3b82f6"
            />
            {primaryColor.trim() && (
              <div
                className="h-10 w-10 shrink-0 rounded-md border"
                style={{ backgroundColor: primaryColor.trim() }}
              />
            )}
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">Accent Color</label>
          <div className="flex items-center gap-2">
            <Input
              value={accentColor}
              onChange={(e) => setAccentColor(e.target.value)}
              placeholder="#8b5cf6"
            />
            {accentColor.trim() && (
              <div
                className="h-10 w-10 shrink-0 rounded-md border"
                style={{ backgroundColor: accentColor.trim() }}
              />
            )}
          </div>
        </div>

        <div className="space-y-1 border-t pt-4">
          <label className="text-sm font-medium">Font family (optional)</label>
          <Input
            value={fontFamily}
            onChange={(e) => setFontFamily(e.target.value)}
            placeholder='"Inter", "Helvetica Neue", sans-serif'
          />
          <p className="text-xs text-muted-foreground">
            Override the default Inter stack with a custom CSS font-family
            string.
          </p>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">
            Theme tokens (advanced)
          </label>
          <p className="text-xs text-muted-foreground">
            Per-mode CSS variable overrides. Keys must start with{' '}
            <code>--color-</code> or <code>--radius-</code>. Example:
            <code>{'{"light": {"--color-primary": "#F38281"}}'}</code>
          </p>
          <textarea
            value={themeTokensJson}
            onChange={(e) => setThemeTokensJson(e.target.value)}
            rows={12}
            spellCheck={false}
            placeholder='{"light": {"--color-primary": "#F38281"}, "dark": {...}}'
            className="w-full rounded-md border bg-background p-2 font-mono text-xs"
          />
          {themeTokensError && (
            <p className="text-xs text-destructive">{themeTokensError}</p>
          )}
        </div>

        <div className="flex justify-end pt-2">
          <Button type="submit" size="sm" disabled={saving}>
            {saving ? 'Saving...' : t('common.save')}
          </Button>
        </div>
      </form>

      {toast && (
        <div className="fixed bottom-4 right-4 z-50 rounded-lg border bg-card px-4 py-3 text-sm shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}
